// src/hr/timeoff/dates.js
// Calendar helpers for the Time Off screens. Dates are 'YYYY-MM-DD' strings handled in UTC so no
// timezone can shift a day; "today" and the clock come from America/New_York like the server.

const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 86400000;
const NY = 'America/New_York';

export const isDateString = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))
  && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

const toUTC = (date) => new Date(`${date}T00:00:00Z`);
const fromUTC = (d) => d.toISOString().slice(0, 10);

function nyParts(now) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NY, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
  }).formatToParts(now);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value;
  return { year: get('year'), month: get('month'), day: get('day'), hour: Number(get('hour')) % 24 };
}

// 'YYYY-MM-DD' for today in New York.
export function todayNY(now = new Date()) {
  const { year, month, day } = nyParts(now);
  return `${year}-${month}-${day}`;
}

export function hourNY(now = new Date()) {
  return nyParts(now).hour;
}

export function greetingFor(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function addDays(date, days) {
  const d = toUTC(date);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUTC(d);
}

export const weekdayOf = (date) => toUTC(date).getUTCDay(); // 0 = Sunday
export const isWeekend = (date) => weekdayOf(date) === 0 || weekdayOf(date) === 6;
export const daysBetween = (from, to) => Math.round((toUTC(to) - toUTC(from)) / DAY_MS);

// Monday to Friday dates in from..to (inclusive), skipping any date in `holidays` (a Set).
export function workdaysBetween(from, to, holidays = new Set()) {
  if (!isDateString(from) || !isDateString(to) || to < from) return [];
  const days = [];
  // A benefit year is the most anyone books in one go; stop well past that.
  for (let d = from, n = 0; d <= to && n < 400; d = addDays(d, 1), n += 1) {
    if (!isWeekend(d) && !holidays.has(d)) days.push(d);
  }
  return days;
}

export function nextWorkday(date, holidays = new Set()) {
  let d = addDays(date, 1);
  while (isWeekend(d) || holidays.has(d)) d = addDays(d, 1);
  return d;
}

// Monday of the week `date` is in.
export function mondayOf(date) {
  const day = weekdayOf(date);
  return addDays(date, day === 0 ? -6 : 1 - day);
}

// 'YYYY-MM' helpers for the month calendar.
export const monthOf = (date) => String(date).slice(0, 7);
export const firstOfMonth = (month) => `${month}-01`;
export function lastOfMonth(month) {
  const [y, m] = month.split('-').map(Number);
  return fromUTC(new Date(Date.UTC(y, m, 0)));
}
export function shiftMonth(month, by) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return fromUTC(d).slice(0, 7);
}
export function monthTitle(month) {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS_LONG[m - 1]} ${y}`;
}

// Weekday rows (Mon-Fri) covering a month: [[date x5], ...].
export function monthWeeks(month) {
  const first = firstOfMonth(month);
  const last = lastOfMonth(month);
  const weeks = [];
  for (let monday = mondayOf(first); monday <= last; monday = addDays(monday, 7)) {
    const week = [0, 1, 2, 3, 4].map((i) => addDays(monday, i));
    // Skip a leading week whose weekdays all fall in the previous month (the 1st is a weekend).
    if (week.some((d) => monthOf(d) === month)) weeks.push(week);
  }
  return weeks;
}

const parts = (date) => {
  const d = toUTC(date);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth(), day: d.getUTCDate(), wd: d.getUTCDay() };
};

export const weekdayShort = (date) => WEEKDAYS_SHORT[parts(date).wd];
export const weekdayLong = (date) => WEEKDAYS_LONG[parts(date).wd];
export const monthShort = (date) => MONTHS_SHORT[parts(date).m];
export const dayOfMonth = (date) => parts(date).day;
// 'Nov 3'
export const shortDate = (date) => `${MONTHS_SHORT[parts(date).m]} ${parts(date).day}`;
// 'Tuesday, November 3'
export const longDate = (date) => `${WEEKDAYS_LONG[parts(date).wd]}, ${MONTHS_LONG[parts(date).m]} ${parts(date).day}`;

// A timestamp (ISO string or Date) as a New York calendar date.
export function dateOfTimestamp(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : todayNY(d);
}

// 'Nov 3, 2026' for a timestamp.
export function timestampLabel(value) {
  const date = dateOfTimestamp(value);
  return date ? `${shortDate(date)}, ${date.slice(0, 4)}` : '';
}

// "2 hours ago", "yesterday", "Nov 1" -- for when a request was sent.
export function sentAgo(value, now = new Date()) {
  const at = new Date(value);
  if (!value || Number.isNaN(at.getTime())) return '';
  const minutes = Math.floor((now - at) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const today = todayNY(now);
  const day = todayNY(at);
  if (day === today) {
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  if (day === addDays(today, -1)) return 'yesterday';
  return `on ${shortDate(day)}`;
}
